import { Button, Col, Input, Row, Spin, Table, UploadProps, notification } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { RcFile, UploadChangeParam } from 'antd/es/upload';
import cx from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { batch } from 'react-redux';
import { ScrollType, buildColumn, getFileDetails, handleResponse, removeCommasAndPercentSign } from '../../../../Helper';
import { deviceAdminApi, graphicApi } from '../../../../api';
import { SecuredComponents, SubPermissions, User } from '../../../../model/AccountModel';
import { PageSizeTypeExtended } from '../../../../model/BadgeModel';
import { BeforeUploadPromise, ResponseObjectEntity, ResponseStatusCode } from '../../../../model/CommonModel';
import { DataTableType } from '../../../../model/DeviceAdminModel';
import {
	BadgeDirectoryInfo,
	GraphicActionType,
	GraphicDirectoryInfo,
	GraphicPageMode,
	GraphicStatus,
	GraphicType,
	SpaceObjectSerialize,
} from '../../../../model/GraphicModel';
import { useStoreDispatch } from '../../../../store';
import { setGraphicActionType, setMapDirectoryDataAction, setMapMode, setSpaceObjectDataAction } from '../../../../store/graphic/actions';
import { Modal, SelectOption } from '../../../common';
import { clearSpaceObjectsAndRedLines } from '../../../graphic/helper';
import styles from './withNewGraphicModal.module.scss';

//Avoid creating object style inline, since increases reconciliations
const scroll: ScrollType = { y: 156 };
const maskStyle: React.CSSProperties = { backgroundColor: '#dcdcdcb8' };
const closeIconStyle: React.CSSProperties = { color: 'white', fontSize: '16px' };
const graphicFileTypes: string[] = ['.vbd', '.bmp', '.gif', '.jpg', '.jpeg', '.png', '.dxf'];
const graphicTypeVelocityCore = GraphicType.VelocityCore;
const presetOptions: SelectOption = [
	{ id: 'edgeToEdgeOption', label: _('EdgeToEdge'), value: PageSizeTypeExtended.EdgeToEdge.toString() },
	{ id: 'borderedOption', label: _('Bordered'), value: PageSizeTypeExtended.WhiteBorder.toString() },
];

enum ModalType {
	New = 0,
	NewSkipExisting,
	ExistingSelection,
}

type WithNewGraphicActions = {
	uploadProps: UploadProps;
	handleOnOpenNewModal: () => void;
	handleOnOpen: (data: Partial<GraphicDirectoryInfo>) => Promise<void>;
};

type DataType = DataTableType<GraphicDirectoryInfo>;

const columns: ColumnsType<DataType> = [
	{
		...buildColumn(_('Name'), 'Name', '80px', 'start'),
		sorter: (a, b) => a.Name.localeCompare(b.Name),
	},
];

const user: User = getUser();
const hasUseMapDesignMode = User.getSubComponentPermission(user, SubPermissions.Maps_Maps_UseDesignMode).allowed;

const WithNewGraphicModal = <T extends {}>(WrappedComponent: React.ComponentType<T & WithNewGraphicActions>) => {
	const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';

	const ComponentWithNewGraphicModal: React.FC<T & WithNewGraphicActions> = props => {
		const dispatch = useStoreDispatch();
		const [visible, setVisible] = useState<boolean>(false);
		const [isModalLoading, setModalIsLoading] = useState<boolean>(false);
		const [isImport, setIsImport] = useState<boolean>(false);
		const [graphicId, setGraphicId] = useState<number>(0);
		const [graphicType, setGraphicType] = useState<GraphicType>(GraphicType.None);
		const [modalType, setModalType] = useState<ModalType>(ModalType.New);
		const [duplicatedData, setDuplicatedData] = useState<GraphicDirectoryInfo[] | BadgeDirectoryInfo[]>([]);
		const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
		const [emptyNameError, setEmptyNameError] = useState<string>('');
		const [hasEmptyNameError, setHasEmptyNameError] = useState<boolean>(false);
		const [preset, setPreset] = useState<string>(PageSizeTypeExtended.EdgeToEdge.toString());

		const [graphicName, setGraphicName] = useState<string>('');
		const graphicNameRef = useRef<string>(graphicName);
		const setGraphicNameRef = (newGraphicNameRef: string): void => {
			graphicNameRef.current = newGraphicNameRef;
			setGraphicName(newGraphicNameRef);
		};
		const getGraphicNameRef = (): string => {
			return graphicNameRef.current;
		};

		const beforeUploadPromise = useRef<BeforeUploadPromise>();

		useEffect(() => {
			deviceAdminApi.getErrorMessages(SecuredComponents.Maps).then(res => {
				setEmptyNameError(res.EmptyName);
			});
		}, []);

		const objectType: string = 'Map';

		const uploadProps: UploadProps = {
			accept: graphicFileTypes.join(','),
			name: 'file',
			multiple: false,
			beforeUpload: (file: RcFile) => {
				if (file) {
					const { nameWithoutExtension, extension } = getFileDetails(file.name);
					if (!extension || graphicFileTypes.indexOf(extension.toLowerCase()) < 0) {
						notification['error']({
							message: _('UnsupportedFileType'),
						});
						return false;
					}

					batch(() => {
						setVisible(true);
						setGraphicNameRef(nameWithoutExtension);
						setIsImport(true);
					});
					return new Promise((resolve, reject) => {
						beforeUploadPromise.current = {
							reject,
							resolve,
						};
					});
				} else {
					return false;
				}
			},
			customRequest: ({ file, onSuccess, onError }) => {
				const formData = new FormData();
				const { name } = file as RcFile;
				const { extension } = getFileDetails(name);
				if (extension) {
					formData.append('file', file);
					formData.append('mapName', getGraphicNameRef() + extension);
					const api = graphicApi.importMapDrawing;
					api(formData)
						.then(response => {
							if (!handleResponse(response)) {
								onSuccess(response);
							} else {
								onError(
									{
										status: response.ResponseStatusCode,
										method: 'POST',
										url: '',
										name: '',
										message: response.ErrorMessage,
									},
									response
								);
							}
						})
						.catch(err => {
							onError(err);
						});
				}
			},
			onChange(info: UploadChangeParam) {
				const {
					file: { status },
				} = info;

				if (info?.file?.response) {
					const response: ResponseObjectEntity<any> = info.file.response as ResponseObjectEntity<SpaceObjectSerialize>;
					const {
						Entity: { Id, DBName, Status },
					} = response;

					const { nameWithoutExtension } = getFileDetails(DBName);
					handleGraphicsLoadResponse(response, Id, nameWithoutExtension, GraphicType.Web, Status, status);
					restorePage();
				}
			},
			showUploadList: false,
		};

		const handleOnOpenNewModal = () => {
			batch(() => {
				setGraphicId(0);
				setGraphicNameRef('');
				setGraphicType(GraphicType.Web);
				setVisible(true);
				setIsImport(false);
			});
		};

		const handleGraphicsLoadResponse = <T extends {}>(
			response: ResponseObjectEntity<T>,
			graphicId: number,
			graphicName: string,
			graphicType: GraphicType,
			graphicStatus?: GraphicStatus,
			status: string = 'done'
		): boolean => {
			let success: boolean = false;
			if (!status?.trim()) {
				status = 'done';
			}
			status = status.toLowerCase().trim();

			let graphicDirectoryData: Partial<GraphicDirectoryInfo> = {
				Id: graphicId,
				Name: graphicName,
				Status: hasUseMapDesignMode ? graphicStatus : GraphicStatus.Published,
			};
			if (response.ResponseStatusCode === ResponseStatusCode.FailedValidation) {
				notification['error']({
					message: _(response.ErrorMessage),
				});
				batch(() => {
					setGraphicId(graphicId);
					setGraphicNameRef(graphicName);
					setGraphicType(graphicType);
					setVisible(true);
					setIsImport(false);
				});
			} else if (status === 'error' || handleResponse(response)) {
				batch(() => {
					setGraphicId(graphicId);
					setGraphicNameRef(graphicName);
					setGraphicType(graphicType);
				});
			} else if (status === 'done' && response.ResponseStatusCode === ResponseStatusCode.Success) {
				success = true;
				clearSpaceObjectsAndRedLines();
				batch(() => {
					beforeUploadPromise.current = undefined;
					setGraphicId(0);
					setGraphicNameRef('');
					setGraphicType(GraphicType.None);
					setVisible(false);
					setIsImport(false);
					dispatch(setSpaceObjectDataAction(response.Entity));
					dispatch(setMapMode(graphicStatus === GraphicStatus.Draft ? GraphicPageMode.Design : GraphicPageMode.Live));
					dispatch(setGraphicActionType(GraphicActionType.Canvas));
					dispatch(setMapDirectoryDataAction(graphicDirectoryData));
				});
			}

			return success;
		};

		const handleOnGoBackToDirectory = () => {
			batch(() => {
				setGraphicId(0);
				setGraphicNameRef('');
				setVisible(false);
				setIsImport(false);
				setModalIsLoading(false);
				setGraphicType(GraphicType.None);
				setModalType(ModalType.New);
				setDuplicatedData([]);
				setSelectedRowKeys([]);
				if (beforeUploadPromise && beforeUploadPromise.current) {
					beforeUploadPromise.current.reject();
				}
			});
		};

		const migrateVelocityCoreBadge = async (currentBadgeId: number, newName: string): Promise<void> => {
			if (modalType !== ModalType.NewSkipExisting) {
				const convertBadgesResponse = await graphicApi.getConvertedBadges(currentBadgeId);
				const {
					Entity: { BadgeDirectoryInfo },
				} = convertBadgesResponse;

				if (!handleResponse(convertBadgesResponse)) {
					if (BadgeDirectoryInfo.length) {
						batch(() => {
							setDuplicatedData(BadgeDirectoryInfo);
							setSelectedRowKeys([BadgeDirectoryInfo[0].Id]);
							setGraphicId(currentBadgeId);
							setGraphicNameRef(newName);
							setModalType(ModalType.ExistingSelection);
							setGraphicType(graphicTypeVelocityCore);
							setVisible(true);
							setIsImport(false);
						});
						return;
					}
				} else {
					batch(() => {
						setGraphicId(currentBadgeId);
						setGraphicNameRef(newName);
						setGraphicType(graphicTypeVelocityCore);
						setVisible(true);
						setIsImport(false);
					});
					return;
				}
			}

			const responseDuplicate = await graphicApi.checkDuplicatedName(newName, false);
			if (responseDuplicate.ResponseStatusCode === ResponseStatusCode.FailedValidation) {
				notification['error']({
					message: _(responseDuplicate.ErrorMessage),
				});
				batch(() => {
					setGraphicId(currentBadgeId);
					setGraphicNameRef(newName);
					setGraphicType(graphicTypeVelocityCore);
					setVisible(true);
					setIsImport(false);
				});
			} else if (!handleResponse(responseDuplicate)) {
				return graphicApi.getBadge({ Id: currentBadgeId, Type: graphicTypeVelocityCore, Name: newName }).then(responseGenerate => {
					const {
						Entity: { Id },
					} = responseGenerate;
					handleGraphicsLoadResponse(responseGenerate, Id, newName, graphicTypeVelocityCore, GraphicStatus.Published);
				});
			} else {
				batch(() => {
					setGraphicId(currentBadgeId);
					setGraphicNameRef(newName);
					setGraphicType(graphicTypeVelocityCore);
					setVisible(true);
					setIsImport(false);
				});
			}
		};

		const migrateVelocityCoreMap = async (currentMapId: number, newName: string): Promise<void> => {
			if (modalType !== ModalType.NewSkipExisting) {
				const convertedMapsResponse = await graphicApi.getConvertedMaps(currentMapId);
				if (!handleResponse(convertedMapsResponse)) {
					const {
						Entity: { MapDirectoriesInfo },
					} = convertedMapsResponse;

					if (MapDirectoriesInfo.length) {
						batch(() => {
							setDuplicatedData(MapDirectoriesInfo);
							setSelectedRowKeys([MapDirectoriesInfo[0].Id]);
							setGraphicId(currentMapId);
							setGraphicNameRef(newName);
							setModalType(ModalType.ExistingSelection);
							setGraphicType(graphicTypeVelocityCore);
							setVisible(true);
							setIsImport(false);
						});
						return;
					}
				} else {
					batch(() => {
						setGraphicId(currentMapId);
						setGraphicNameRef(newName);
						setGraphicType(graphicTypeVelocityCore);
						setVisible(true);
						setIsImport(false);
					});
					return;
				}
			}

			const responseDuplicate = await graphicApi.checkDuplicatedName(newName);
			if (responseDuplicate.ResponseStatusCode === ResponseStatusCode.FailedValidation) {
				notification['error']({
					message: _(responseDuplicate.ErrorMessage),
				});
				batch(() => {
					setGraphicId(currentMapId);
					setGraphicNameRef(newName);
					setGraphicType(graphicTypeVelocityCore);
					setVisible(true);
					setIsImport(false);
				});
			} else if (!handleResponse(responseDuplicate)) {
				return graphicApi.getMap({ Id: currentMapId, Type: graphicTypeVelocityCore, Name: newName }).then(responseGenerate => {
					const {
						Entity: { Id, Status },
					} = responseGenerate;
					handleGraphicsLoadResponse(responseGenerate, Id, newName, graphicTypeVelocityCore, Status);
				});
			} else {
				batch(() => {
					setGraphicId(currentMapId);
					setGraphicNameRef(newName);
					setGraphicType(graphicTypeVelocityCore);
					setVisible(true);
					setIsImport(false);
				});
			}
		};

		const restorePage = (hasEmptyError: boolean = false) => {
			batch(() => {
				setHasEmptyNameError(hasEmptyError);
				setModalIsLoading(false);
			});
		};

		const handleOnOpen = (graphicData: Partial<GraphicDirectoryInfo>): Promise<void> => {
			setModalIsLoading(true);
			if (graphicData.Type === GraphicType.VelocityCore) {
				const migrate = migrateVelocityCoreMap;
				return migrate(graphicData.Id, graphicData.Name).finally(() => {
					restorePage();
				});
			} else {
				return graphicApi
					.getMap(graphicData)
					.then(response => {
						const {
							Entity: { Id, Status },
						} = response;
						handleGraphicsLoadResponse(response, Id, graphicData.Name, graphicData.Type, Status, '');
					})
					.finally(() => {
						restorePage();
					});
			}
		};

		const handleOnOverrideMap = () => {
			const mapTypeVelocityCore = GraphicType.VelocityCore;
			setModalIsLoading(true);
			const overrideData = duplicatedData.find(x => x.Id === (selectedRowKeys[0] as number));
			graphicApi
				.getMap({ Id: graphicId, Type: mapTypeVelocityCore, Name: overrideData.Name }, overrideData.Id)
				.then(responseGenerate => {
					const {
						Entity: { Id, Status },
					} = responseGenerate;
					handleGraphicsLoadResponse(responseGenerate, Id, overrideData.Name, mapTypeVelocityCore, Status);
				})
				.finally(() => {
					restorePage();
				});
		};

		const handleOnOverrideBadge = () => {
			const badgeTypeVelocityCore = GraphicType.VelocityCore;
			setModalIsLoading(true);
			const overrideData = duplicatedData.find(x => x.Id === (selectedRowKeys[0] as number));
			graphicApi
				.getBadge({ Id: graphicId, Type: badgeTypeVelocityCore, Name: overrideData.Name }, overrideData.Id)
				.then(responseGenerate => {
					const {
						Entity: { Id },
					} = responseGenerate;
					handleGraphicsLoadResponse(responseGenerate, Id, overrideData.Name, badgeTypeVelocityCore, GraphicStatus.Published);
				})
				.finally(() => {
					restorePage();
				});
		};

		const handleOnAddNewGraphic = () => {
			setModalIsLoading(true);
			if (modalType === ModalType.ExistingSelection) {
				setModalType(ModalType.NewSkipExisting);
				restorePage();
			} else {
				if (graphicName) {
					if (!isImport) {
						if (graphicType === GraphicType.VelocityCore) {
							const migrate = migrateVelocityCoreMap;
							migrate(graphicId, graphicName).finally(() => {
								restorePage();
							});
						} else {
							graphicApi
								.createNewMap(graphicName)
								.then(response => {
									const {
										Entity: { Id, Status },
									} = response;
									handleGraphicsLoadResponse(response, Id, graphicName, GraphicType.Web, Status);
								})
								.finally(() => {
									restorePage();
								});
						}
					} else {
						graphicApi
							.checkDuplicatedName(graphicName)
							.then(response => {
								if (response.ResponseStatusCode === ResponseStatusCode.FailedValidation) {
									notification['error']({
										message: _(response.ErrorMessage),
									});
									restorePage();
								} else if (!handleResponse(response)) {
									beforeUploadPromise.current.resolve();
								}
							})
							.catch(() => {
								beforeUploadPromise.current.reject();
							});
					}
				} else {
					restorePage(true);
				}
			}
		};

		const handleOnSetNewName = (e: React.ChangeEvent<HTMLInputElement>) => {
			const name = removeCommasAndPercentSign(e.currentTarget.value);
			setGraphicNameRef(name);
		};

		const handleOnSetPreset = value => setPreset(value);

		let actionButtons: React.ReactNode[] = [
			<Button id={`goBack${objectType}DirectoryButton`} key='cancel' type='link' disabled={isModalLoading} onClick={handleOnGoBackToDirectory}>
				{_(`GoBack${objectType}Directory`)}
			</Button>,
			<Button id={`new${objectType}Button`} key='save' type='primary' disabled={isModalLoading} onClick={handleOnAddNewGraphic}>
				{_(`New${objectType}`)}
			</Button>,
		];

		let content: React.ReactNode = <></>;
		switch (modalType) {
			case ModalType.New:
			case ModalType.NewSkipExisting: {
				content = (
					<>
						<Row align='middle'>
							<Col flex={'170px'}>
								<label id={`graphicNameForNew${objectType}Label`} htmlFor={`graphicNameForNew${objectType}Input`}>
									{_(`NameForNewMap`)}:
								</label>
							</Col>
							<Col flex='auto'>
								<Input
									id={`graphicNameForNew${objectType}Input`}
									className={cx({ [styles.inputError]: hasEmptyNameError })}
									maxLength={50}
									onChange={handleOnSetNewName}
									value={graphicName}
								/>
								{hasEmptyNameError && (
									<label id='graphicNameErrorMessage' htmlFor={`graphicNameForNew${objectType}Input`} className={styles.graphicsErrorMessage}>
										{emptyNameError}
									</label>
								)}
							</Col>
						</Row>
					</>
				);
				break;
			}
			case ModalType.ExistingSelection: {
				actionButtons.splice(
					2,
					0,
					<Button id={`override${objectType}Button`} key='override' type='primary' disabled={isModalLoading} onClick={handleOnOverrideMap}>
						{_(`Override${objectType}`)}
					</Button>
				);
				content = (
					<div className={styles.graphicsModalInstructions}>
						<span>{_(`Override${objectType}Instructions`)}</span>
						<br />
						<Table
							id={`override${objectType}Table`}
							key={`override${objectType}`}
							rowSelection={{
								onChange: (selectedRowKeys: React.Key[], selectedRows: DataType[]) => {
									setSelectedRowKeys(selectedRowKeys);
								},
								getCheckboxProps: (record: DataType) => ({
									id: `override${objectType}RadioCheckBox-${record.Id}`,
								}),
								selectedRowKeys,
								type: 'radio',
							}}
							columns={columns}
							dataSource={duplicatedData.map<DataType>(x => ({
								...x,
								key: x.Id,
							}))}
							scroll={scroll}
							className={styles.graphicsModalTable}
							pagination={false}
							size='small'
							onRow={(record: DataType) => ({
								onClick: () => {
									setSelectedRowKeys([record.Id]);
								},
							})}
							onHeaderRow={() => ({ id: `override${objectType}Header` })}
							getPopupContainer={() => document.getElementById(`override${objectType}Header`)}
						/>
					</div>
				);
				break;
			}
		}

		return (
			<>
				<Modal
					keyboard={false}
					maskClosable={false}
					onCancel={handleOnGoBackToDirectory}
					onClickOk={() => null}
					width={600}
					title={<span>{_(`New${objectType}`)}</span>}
					visible={visible}
					customZoomClass={styles.graphicsModalDisplayContainer}
					className={cx(styles.graphicsModalContainer, { [styles.graphicsModalDisplayContainer]: false })}
					maskStyle={maskStyle}
					footer={actionButtons}
					closeIconStyle={closeIconStyle}>
					<Spin spinning={isModalLoading} tip={_('Loading')}>
						{content}
					</Spin>
				</Modal>
				<WrappedComponent {...(props as T)} uploadProps={uploadProps} handleOnOpen={handleOnOpen} handleOnOpenNewModal={handleOnOpenNewModal} />
			</>
		);
	};

	ComponentWithNewGraphicModal.displayName = `withNewGraphicModal(${displayName})`;

	return ComponentWithNewGraphicModal;
};

export { WithNewGraphicModal };
